import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import type { RepoDoc, RepoDocs, RepoDocRow } from '../api/types'
import { ErrorState, Loading } from '../components/layout'
import { useDocumentTitle } from '../components/useDocumentTitle'
import { Markdown } from '../markdown/Markdown'
import { contentNodes } from '../plans/html'
import { useSelectedProject, useStore } from '../store/context'
import { htmlContent, markdownOutline } from './content'
import { docsPath, resolveRepoUrl } from './links'
import './docs.css'

export function DocsPage() {
  const project = useSelectedProject()
  const path = useParams()['*'] ?? ''
  if (!project) return <div className="state-message">Choose a project.</div>
  return <Reader key={project.id} project={project.id} projectKey={project.key} path={path} />
}
function Reader({ project, projectKey, path }: { project: string; projectKey: string; path: string }) {
  const store = useStore()
  const [tree, setTree] = useState<RepoDocs | null>(null)
  const [doc, setDoc] = useState<RepoDoc | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [refresh, setRefresh] = useState(0)
  const article = useRef<HTMLElement>(null)
  useDocumentTitle(doc?.title ?? 'Docs')
  useEffect(() => {
    let live = true
    setError(null)
    store.repoDocs(project).then(t => { if (live) setTree(t) }, e => { if (live) setError(e) })
    return () => { live = false }
  }, [store, project, refresh])
  const sha = tree?.source?.source_sha
  useEffect(() => {
    let live = true
    setDoc(null)
    setError(null)
    if (path && sha) store.repoDoc(project, path, sha).then(d => { if (live) setDoc(d) }, e => { if (live) setError(e) })
    return () => { live = false }
  }, [store, project, path, sha, refresh])
  const ctx = useMemo(() => ({ path, project: projectKey, sha: sha ?? '', repository: tree?.source?.repository ?? null, docs: new Set(tree?.docs.map(d => d.path)) }), [path, projectKey, sha, tree])
  const resolveUrl = useMemo(() => (url: string, kind: 'link' | 'image') => resolveRepoUrl(url, kind, ctx), [ctx])
  const html = useMemo(() => doc?.kind === 'html' ? htmlContent(doc.body, ctx) : null, [doc, ctx])
  const headings = useMemo(() => html?.headings ?? markdownOutline(doc?.body ?? ''), [html, doc])
  const current = doc?.path === path && doc.source_sha === sha ? doc : null
  if (!path && tree?.source && tree.docs.length) return <Navigate to={docsPath(projectKey, tree.docs.some(d => d.path === 'README.md') ? 'README.md' : tree.docs[0]?.path ?? '')} replace />
  const jump = (id: string) => article.current?.querySelector<HTMLElement>(`[id="${CSS.escape(id)}"]`)?.scrollIntoView({ block: 'start' })
  return <div className="docs-page">
    <aside className="docs-sidebar" aria-label="Repository docs">
      <div className="docs-tree-head"><strong>Repository docs</strong><button className="button button-quiet" onClick={() => setRefresh(n => n + 1)}>Refresh</button></div>
      <nav aria-label="Docs"><DocTree docs={tree?.docs ?? []} folder="" project={projectKey} current={path} headings={headings} jump={jump} /></nav>
    </aside>
    <article className="docs-reader" ref={article}>
      {error ? error.status === 404 ? <div className="state-message"><h1>No such doc</h1><p><code>{path}</code> is not among the repository’s docs.</p></div> : <ErrorState error={error} onRetry={() => setRefresh(n => n + 1)} />
        : !tree ? <Loading /> : !tree.source ? <div className="state-message"><h1>Docs unavailable</h1><p>Choose the repository folder on a connected runner and fetch its default branch.</p></div>
        : !tree.docs.length ? <p>No Markdown files on the fetched default branch.</p>
        : !current ? <Loading /> : <>
          <header className="docs-meta"><code>{current.path}</code> at <code title={current.source_sha}>{current.source_sha.slice(0, 7)}</code>
            <p>Read-only: edits go through the repository.</p>
            <small>Fetched {tree.source.branch}; cached {new Date(tree.source.read_at).toLocaleString()}.</small>
            {tree.source.truncated && <p>Some docs exceed the size or count limits.</p>}
          </header>
          {html ? <div className="markdown-body">{contentNodes(html.nodes, [], 'plain')}</div> : <Markdown source={current.body} resolveUrl={resolveUrl} localImagesOnly />}
        </>}
    </article>
  </div>
}

function DocTree({docs, folder, project, current, headings, jump}: {docs: readonly RepoDocRow[]; folder: string; project: string; current: string; headings: readonly {id: string; text: string; level: number}[]; jump: (id: string) => void}) {
  const files = docs.filter(d => !d.path.slice(folder.length).includes('/'))
  const folders = [...new Set(docs.map(d => d.path.slice(folder.length)).filter(p => p.includes('/')).map(p => p.split('/')[0]!))]
  return <>
    {files.map(d => <div key={d.path} className="docs-entry">
      <Link to={docsPath(project, d.path)} aria-current={current === d.path ? 'page' : undefined} title={d.path}>{d.title}</Link>
      <small>{d.path.slice(folder.length)}</small>
      {current === d.path && headings.length > 0 && <nav aria-label="Outline"><ul>{headings.map(h => <li key={h.id} style={{marginInlineStart:`${h.level - 1}ch`}}><button onClick={() => jump(h.id)}>{h.text}</button></li>)}</ul></nav>}
    </div>)}
    {folders.map(name => { const prefix = `${folder}${name}/`; return <details className="docs-folder" key={prefix} open><summary>{name}</summary><DocTree docs={docs.filter(d => d.path.startsWith(prefix))} folder={prefix} project={project} current={current} headings={headings} jump={jump} /></details> })}
  </>
}
